import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {fetchTranslations} from "../types/translationsClients";
import DocklineFX from "../components/DocklineFX";
import HeroFX from "../components/HeroFX";
import { Helmet } from 'react-helmet-async';

const Studios = () => {
    const navigate = useNavigate();

    const [dict, setDict] = useState<Record<string, string> | null>(null);
    const [logos, setLogos] = useState<string[]>([]);
    const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
    const locale = "de";
    const namespace = "landing";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchTranslations({
                    baseUrl: process.env.REACT_APP_API_BASE_URL || "",
                    locale,
                    namespace
                });
                if (!cancelled) setDict(data.items);
            } catch (e) {
                if (!cancelled) setDict({});
                console.error("Failed to load translations (landing)", e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const bgStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none"
    };

  // Landing hero parallax + tilt on scroll with visibility toggle
  useEffect(() => {
    const hero = document.querySelector('.landing-hero-cover') as HTMLElement | null;
    if (!hero) return;

    const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

    let rafId = 0;
    const loop = () => {
      const doc = document.documentElement;
      const st = doc.scrollTop || document.body.scrollTop || 0;

      // Calculate halfway point of the total scrollable distance
      const maxScroll = Math.max(1, doc.scrollHeight - doc.clientHeight);
      const hideThreshold = 0.5 * maxScroll; // hide beyond 50% of page height

      if (st >= hideThreshold) {
        // Hide hero when scrolled beyond threshold
        hero.classList.add('lh-hidden');
      } else {
        // Show hero and continue parallax/tilt while above threshold
        hero.classList.remove('lh-hidden');

        const vh = window.innerHeight || 1;
        // Map the first ~140% of viewport height to 0..1 progress (slower progression)
        const t = clamp(st / (vh * 1.4));

        // Smaller effect so it lasts longer: scale 1.0 → 0.90, rotateX 0deg → 6deg
        const scale = 1 - 0.20 * t;
        const tilt = 6 * t;

        // Keep anchored while adding perspective, scaling, and tilt
        hero.style.transform = `translateX(-50%) perspective(1000px) scale(${scale}) rotateX(${tilt}deg)`;
      }

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => {
      hero.classList.remove('lh-hidden');
      cancelAnimationFrame(rafId);
    };
  }, []);

    return (
        <>
        <Helmet>
            <title>Fotografie & Videoproduktion in München | Dockline Studios</title>
            <meta
                name="description"
                content="Dockline Studios bietet professionelle Fotografie, Videoproduktion, Headshots, Event Coverage und Livestreaming in München und Deutschland."
            />
            <meta
                name="keywords"
                content="Fotograf München, Videoproduktion München, Eventfotograf München, Headshots München, Imagefilm München, Livestream Produktion München, Dockline Studios"
            />
            <link rel="canonical" href="https://dockline.digital/studios" />

            {/* Open Graph */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content="Dockline Studios – Foto & Videoproduktion in München" />
            <meta
                property="og:description"
                content="Professionelle Fotografie, Video, Livestreaming und Headshots in München. Dockline Studios macht visuelle Inhalte, die wirken."
            />
            <meta property="og:url" content="https://dockline.digital/studios" />
            <meta property="og:image" content="https://dockline.digital/pics/og/studios-og.jpg" />
            <meta property="og:locale" content="de_DE" />

            {/* JSON-LD schema for Photography / Video Service */}
            <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Dockline Studios",
              "url": "https://dockline.digital/studios",
              "logo": "https://dockline.digital/pics/logo/sec/dstudios_logo_sec.svg",
              "serviceType": "Fotografie & Videoproduktion",
              "description": "Fotografie, Video, Headshots, Event Coverage und Livestreaming in München.",
              "areaServed": {
                "@type": "City",
                "name": "München",
                "addressCountry": "DE"
              },
              "provider": {
                "@type": "Organization",
                "name": "Dockline Digital"
              }
            }
            `}</script>
            {/* JSON-LD FAQ schema */}
            <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What kind of projects does Dockline Studios cover?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Dockline Studios covers a wide range of visual projects: business photography, event coverage, headshots, image-style videos, social media clips and live production. We help you choose a format that fits your brand, channels and goals."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you only work in Munich?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Our production focus is in and around Munich, but we also work with clients from other regions. Many projects can be planned on site in Munich and then used across Germany and beyond. For editing and post-production we can collaborate fully remotely."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How is photography and video production priced?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Pricing depends on factors like preparation time, shooting duration, crew size and the amount of editing required. Typical projects are calculated as a clear project fee or day rate, based on your brief. After a short call you receive a transparent proposal with scope, deliverables and timeline."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can you help if we do not yet have a concrete concept?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Many projects start with only a rough idea. We help you clarify goals, target audience and key messages, then translate that into a simple concept, shot list and schedule so that everyone knows what we are creating on the shoot day."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do you also take care of editing and delivery for web and social media?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We handle the full post-production process: selection, colour correction, retouching, sound, graphics and exports. Final files are delivered in the formats you need for website, LinkedIn, Instagram or internal use, so you can publish without extra technical work."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can you work with footage or photos we already have?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. We can build new edits from existing footage, improve old cuts, adapt content for other platforms or refresh outdated team photos. This is often a cost-effective way to modernise your visual presence without a full reshoot."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How far in advance should we book a shoot?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "For events and larger productions it is best to book as early as possible so we can reserve the date and plan calmly. Smaller headshot or content sessions can sometimes be arranged at shorter notice, depending on availability. The earlier you reach out, the more options we have."
                  }
                }
              ]
            }
            `}</script>
        </Helmet>

        <div id="studios-page" style={{'--fx-color': '#FF5A52', '--fx-glow': 'rgba(255,90,82,0.32)'} as React.CSSProperties}>
            <div className="landing-hero-cover hero-anim">
                <img src="/pics/landingPref4Studios.webp" alt="" className="landing-hero" id="landing-hero" />
                <HeroFX/>
                <div className="landing-hero-centered-text">
                    <h1 className="studio-color">Dockline Studios</h1>
                    <h2>Visual Content Engineering</h2>
                </div>
                <a
                  className="landing-hero-bottom-hint"
                  onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
                  style={{ cursor: 'pointer' }}
                >
                  Scroll to Explore
                </a>
            </div>

            <div className="content">
                <DocklineFX color="#FF5A52"/>

                {/* === Studios Intro === */}
                <section className="studios-intro">
                    <div className="section-intro">
                        <h2 className="studio-color">What Dockline Studios Does</h2>
                        <p>We plan, capture, and finish visual stories - Dockline Studios creates photography and video content for companies, teams, and events. We produce clear, modern visuals designed for websites, social media, marketing, and brand communication.</p>
                    </div>
                </section>

                <div className="munich-local-link">
                    <Link to="/fotograf-muenchen" className="local-link-button">
                        Fotograf in München →
                    </Link>
                </div>

                {/* === Services (3-up) === */}
                <section className="studios-services" aria-labelledby="studios-services-title">
                    <div className="section-intro">
                        <h2 id="studios-services-title">Services</h2>
                    </div>
                    <div className="studios-services__cards">
                        <article className="studios-card">
                            <i className="bi bi-camera-video"></i>
                            <h3>Event Photo & Video</h3>
                            <p>Conference, team offsite, product launch — we cover the moments and the atmosphere with a clean, consistent look.</p>
                        </article>
                        <article className="studios-card">
                            <i className="bi bi-person-badge"></i>
                            <h3>Personalized Headshots</h3>
                            <p>Website-ready portraits that match your brand: lighting, backdrop, and guidance for relaxed, confident images.</p>
                        </article>
                        <article className="studios-card">
                            <i className="bi bi-film"></i>
                            <h3>Small Marketing Clips</h3>
                            <p>30–90s social-ready clips and short explainers — fast turnaround, on-brand graphics, and subtitles.</p>
                        </article>
                        <article className="studios-card">
                            <i className="bi bi-broadcast"></i>
                            <h3>Live Production & Streaming</h3>
                            <p>Professional multi‑camera live production and streaming with reliable audio, lower‑thirds, and recording for later reuse.</p>
                        </article>
                    </div>
                </section>

                {/* === Reference Work (overview + gallery) === */}
                <section className="studios-references" aria-labelledby="studios-references-title">
                    <div className="section-intro">
                        <h2 id="studios-references-title">Reference Work</h2>
                        <p>A quick look at what we create</p>
                    </div>

                    {/* Overview: one example for each media type */}
                    <div className="studios-overview__grid">
                        <figure className="media-frame">
                            <img src="/pics/subway.webp" alt="Landscape photo reference" />
                            <figcaption>Architectural Photography</figcaption>
                        </figure>
                        <figure className="media-frame">
                            <img src="/pics/headshots2.jpeg.webp" alt="Headshot reference" />
                            <figcaption>Personalized Headshots</figcaption>
                        </figure>
                        <figure className="media-frame">
                            <img src="/pics/event3.jpeg.webp" alt="Event photo reference" />
                            <figcaption>Event Photography</figcaption>
                        </figure>
                        <figure className="media-frame">
                            <img src="/pics/livestream.jpeg.webp" alt="Live production still" />
                            <figcaption>Live Production & Streaming</figcaption>
                        </figure>
                    </div>

                    {/*/!* Flexible gallery with thumbnails and lightbox *!/*/}
                    {/*<div className="studios-gallery-flex">*/}
                    {/*    <div className="section-intro">*/}
                    {/*        <h2>Gallery</h2>*/}
                    {/*        <p>Explore more photos by Lucas Brand</p>*/}
                    {/*    </div>*/}
                    {/*    <div className="studios-gallery__thumbs">*/}
                    {/*        {[*/}
                    {/*          '/pics/studios/event-placeholder-1.jpg',*/}
                    {/*          '/pics/studios/event-placeholder-2.jpg',*/}
                    {/*          '/pics/studios/headshot-placeholder-1.jpg',*/}
                    {/*          '/pics/studios/headshot-placeholder-2.jpg',*/}
                    {/*          '/pics/studios/clip-placeholder-1.jpg',*/}
                    {/*          '/pics/studios/clip-placeholder-2.jpg',*/}
                    {/*          '/pics/studios/landscape-placeholder.jpg',*/}
                    {/*          '/pics/studios/livestream-placeholder.jpg',*/}
                    {/*        ].map((src, idx) => (*/}
                    {/*          <button key={idx} className="thumb" onClick={() => setLightbox({ open: true, index: idx })}>*/}
                    {/*            <img src={src} alt={`Gallery image ${idx + 1}`} />*/}
                    {/*          </button>*/}
                    {/*        ))}*/}
                    {/*    </div>*/}

                    {/*    {lightbox.open && (*/}
                    {/*      <div className="lightbox" role="dialog" aria-modal="true" onClick={() => setLightbox({ open: false, index: 0 })}>*/}
                    {/*        <div className="lightbox__inner" onClick={(e) => e.stopPropagation()}>*/}
                    {/*          <img src={[*/}
                    {/*            '/pics/studios/event-placeholder-1.jpg',*/}
                    {/*            '/pics/studios/event-placeholder-2.jpg',*/}
                    {/*            '/pics/studios/headshot-placeholder-1.jpg',*/}
                    {/*            '/pics/studios/headshot-placeholder-2.jpg',*/}
                    {/*            '/pics/studios/clip-placeholder-1.jpg',*/}
                    {/*            '/pics/studios/clip-placeholder-2.jpg',*/}
                    {/*            '/pics/studios/landscape-placeholder.jpg',*/}
                    {/*            '/pics/studios/livestream-placeholder.jpg',*/}
                    {/*          ][lightbox.index]} alt="Selected work" />*/}
                    {/*          <div className="lightbox__controls">*/}
                    {/*            <button onClick={() => setLightbox((s) => ({ open: true, index: (s.index + 7) % 8 }))} aria-label="Previous image">*/}
                    {/*              <i className="bi bi-chevron-left"></i>*/}
                    {/*            </button>*/}
                    {/*            <button onClick={() => setLightbox({ open: false, index: 0 })} aria-label="Close">*/}
                    {/*              <i className="bi bi-x-lg"></i>*/}
                    {/*            </button>*/}
                    {/*            <button onClick={() => setLightbox((s) => ({ open: true, index: (s.index + 1) % 8 }))} aria-label="Next image">*/}
                    {/*              <i className="bi bi-chevron-right"></i>*/}
                    {/*            </button>*/}
                    {/*          </div>*/}
                    {/*        </div>*/}
                    {/*      </div>*/}
                    {/*    )}*/}
                    {/*</div>*/}
                </section>

                {/* === Tools (editing software screenshots) === */}
                <section className="studios-tools" aria-labelledby="studios-tools-title">
                    <div className="section-intro">
                        <h2 id="studios-tools-title">Editing Workflow</h2>
                        <p>From rough cut to grade to delivery — a streamlined pipeline for fast turnarounds.</p>
                    </div>
                    <div className="studios-tools__grid">
                        <figure className="media-frame">
                            <img src="/pics/davinci.webp" alt="Video editing software screenshot" />
                            <figcaption>Professional Video Editing in DaVinci Resolve</figcaption>
                        </figure>
                        <figure className="media-frame">
                            <img src="/pics/affinity.webp" alt="Photo editing software screenshot" />
                            <figcaption>Professional Photo Editing in Affinity Photo</figcaption>
                        </figure>
                    </div>
                </section>

                {/* === Why Dockline Studios === */}
                <section className="systems-why" aria-labelledby="studios-why-title">
                    <div className="section-intro">
                        <h2 id="studios-why-title" className="studio-color">Why Dockline Studios?</h2>
                        <p>
                            Dockline Studios combines clean, modern visual production with a personal, uncomplicated workflow.
                            You work directly with the person behind the camera — no large crews, no slow communication,
                            just efficient collaboration and reliable results.
                        </p>
                    </div>

                    <div className="systems-why__grid">
                        <article className="systems-card systems-why__item">
                            <h3>Direct collaboration with your photographer & videographer</h3>
                            <p>
                                From concept to delivery, you speak directly with the creator. This ensures fast decisions,
                                flexible planning and visuals that precisely match the story you want to tell —
                                whether it’s for your website, a team event or a marketing clip.
                            </p>
                        </article>

                        <article className="systems-card systems-why__item">
                            <h3>Modern, clean visuals built for digital platforms</h3>
                            <p>
                                Every shoot is optimised for real-world use: websites, LinkedIn, presentations,
                                campaigns and social media. Lighting, framing and editing follow a consistent,
                                contemporary style that works across all channels.
                            </p>
                        </article>

                        <article className="systems-card systems-why__item">
                            <h3>Lean structure, transparent project pricing</h3>
                            <p>
                                Dockline Studios provides professional results without agency overhead.
                                Because the workflow is lean and personal, you benefit from
                                <em> fair, predictable project rates</em> — ideal for companies, events
                                and teams that need high-quality content without inflated production costs.
                            </p>
                        </article>
                    </div>
                </section>

                {/* === FAQ === */}
                <section className="systems-faq" aria-labelledby="studios-faq-title">
                    <div className="section-intro">
                        <h2 id="studios-faq-title" className="studio-color">Frequently Asked Questions</h2>
                        <p>
                            A few common questions about how Dockline Studios works with photography, video and live
                            production projects. If you do not see your situation here, just reach out and we are happy
                            to discuss it.
                        </p>
                    </div>
                    <div className="faq-list">
                        <details className="faq-item">
                            <summary><p className="boldText">What kind of projects does Dockline Studios cover?</p></summary>
                            <p>
                                Dockline Studios covers business photography, event coverage, headshots, small marketing
                                clips, image-style videos and live production. We focus on clear, modern visuals that work
                                across websites, presentations and social media.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Do you only work in Munich?</p></summary>
                            <p>
                                Our production base is in Munich, but we also work with clients from other regions. Many
                                shoots take place in and around Munich, while post-production and feedback can happen fully
                                remote, so you can use the results across Germany and beyond.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">How is photography and video production priced?</p></summary>
                            <p>
                                Pricing depends on preparation time, shooting duration, crew size and the amount of editing
                                required. Typical projects are calculated as a clear project fee or day rate based on your
                                brief. After a short call you receive a transparent proposal with scope, deliverables and
                                timeline.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Can you help if we do not yet have a concrete concept?</p></summary>
                            <p>
                                Yes. Many projects start with only a rough idea. We help you clarify goals, target audience
                                and key messages, then turn that into a simple concept, shot list and schedule so everyone
                                knows what we are creating on the shoot day.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Do you also handle editing and delivery for web and social media?</p></summary>
                            <p>
                                We take care of the full post-production: selection, colour correction, retouching, sound,
                                graphics and exports. Final files are delivered in the formats you need for website,
                                LinkedIn, Instagram or internal channels.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">Can you work with footage or photos we already have?</p></summary>
                            <p>
                                Yes. We can create new edits from existing footage, improve old cuts, adapt content for
                                other platforms or refresh outdated team photos. This is often a cost-effective way to
                                modernise your visual presence without a full reshoot.
                            </p>
                        </details>
                        <details className="faq-item">
                            <summary><p className="boldText">How far in advance should we book a shoot?</p></summary>
                            <p>
                                For events and larger productions it is best to book as early as possible so we can reserve
                                the date and plan calmly. Smaller headshot or content sessions can sometimes be arranged at
                                shorter notice, depending on availability.
                            </p>
                        </details>
                    </div>
                </section>

                {/* === CTA === */}
                <section className="systems-cta">
                    <h2 className="studio-color">Let’s plan your shoot</h2>
                    <p id="systems-cta-p">Events, headshots, or a short marketing clip — tell us what you need and we’ll propose a plan.</p>
                    <Link to="/" state={{scrollTo: 'contact-section'}}><button><p>Get in Touch</p></button></Link>
                </section>
            </div>
        </div>
    </>
    );
};
export default Studios;